/**
 * `IntelPane` — the dock body `AnalysisDock` swaps in once `#intelSelect` names a use case
 * (design §6.1): "the use case block: question + description, a 'How was this generated?'
 * button (`#intelMethodBtn`) opening the methodology section, a Reset (`#intelReset`) that
 * clears result layers and selection."
 *
 * UC1 (Task 3) and UC2 (Task 4) restructure their own result panes into sub-tabs, one of
 * which is Methodology — so `#intelMethodBtn` switches `intel.subTab` to `'method'` for
 * those two rather than toggling a `<Methodology>` block rendered up here. UC3 keeps its
 * original behaviour untouched: the button toggles local `methodOpen` state and the
 * `<Methodology>` block renders beside it, one level up from `Uc3`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppStore } from '../../lib/state';
import { Methodology } from './Methodology';
import { Uc1 } from './Uc1';
import { Uc2 } from './Uc2';
import { Uc3 } from './Uc3';
import { useUseCases } from './api';

export function IntelPane() {
  const { t } = useTranslation();
  const useCase = useAppStore((s) => s.intel.useCase);
  const resetIntel = useAppStore((s) => s.resetIntel);
  const setIntelSubTab = useAppStore((s) => s.setIntelSubTab);
  const [methodOpen, setMethodOpen] = useState(false);
  const { data: useCases } = useUseCases();
  const meta = (useCases ?? []).find((uc) => uc.id === useCase);

  if (!useCase) return null;

  const hasOwnMethodTab = useCase === 'uc1' || useCase === 'uc2';

  return (
    <div className="intel-pane" data-testid="intel-pane">
      {meta ? (
        <div className="intel-head intel-card">
          <h4 className="intel-card-title">{meta.question}</h4>
          {/* Layout round 2 Oct 2026: one muted line, clamped to two — the description is
              orientation, not the panel's content, and at full length it pushed the level
              switch and the chips below the fold on a 450 px dock. The full text stays in
              the DOM (and in the `title`) for anyone who wants it. */}
          <p className="intel-card-sub" title={meta.description}>
            {meta.description}
          </p>
        </div>
      ) : null}
      {hasOwnMethodTab ? null : (
      <div className="ad-actions">
        <button
          type="button"
          className="btn ghost sm"
          id="intelMethodBtn"
          // UC1/UC2: this is a navigation button (it switches `intel.subTab` to the
          // Methodology sub-tab), not a disclosure — `aria-expanded` would announce a
          // control that expands nothing. UC3 still toggles its own panel below.
          aria-expanded={hasOwnMethodTab ? undefined : methodOpen}
          onClick={() => {
            if (hasOwnMethodTab) setIntelSubTab('method');
            else setMethodOpen((v) => !v);
          }}
        >
          {t('intel.methodBtn')}
        </button>
        <button
          type="button"
          className="btn ghost sm"
          id="intelReset"
          onClick={() => {
            resetIntel();
            setMethodOpen(false);
          }}
        >
          {t('intel.resetBtn')}
        </button>
      </div>
      )}
      {useCase === 'uc3' ? <Methodology useCase={useCase} open={methodOpen} /> : null}
      {useCase === 'uc1' ? (
        <Uc1 />
      ) : useCase === 'uc2' ? (
        <Uc2 />
      ) : useCase === 'uc3' ? (
        <Uc3 />
      ) : (
        <div className="note intel-comingsoon">{t('intel.comingSoon')}</div>
      )}
    </div>
  );
}

export default IntelPane;
